import { Injectable, effect, inject, untracked } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, take } from 'rxjs';
import { adminActions } from '@core/actions/admin.actions';
import {
  selectAuthIsIdle,
  selectIsFullyAuthenticated,
  selectIsSyrfAdminUserAuthenticated,
} from './auth.selectors';
import {
  ApplicationCapabilitiesService,
  ApplicationCapabilitiesState,
} from './application-capabilities.service';
import { ApplicationCapabilityName } from './application-capability';

/**
 * The backend decisions to present, or null where PM authority does not decide here (off/shadow
 * answer 404; not yet read; or the read failed), which keeps the claim fallback exactly as
 * before M2a. A refusal (impersonating) or unavailable authority presents nothing as allowed.
 */
export function presentedCapabilities(
  state: ApplicationCapabilitiesState
): Readonly<Record<string, boolean>> | null {
  switch (state.status) {
    case 'loaded': {
      const presented: Record<string, boolean> = {};
      for (const [name, decision] of Object.entries(state.decisions)) {
        presented[name] = decision?.allowed === true;
      }
      return presented;
    }
    case 'refused':
    case 'unavailable':
      return {};
    default:
      return null;
  }
}

/**
 * Keeps the capability presentation current (M2a): reads the caller's capabilities when they
 * sign in, forgets them on sign-out, and mirrors every answer into the store so NgRx selectors,
 * menus and signals all present the same backend decision. Browser state is presentation only.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationAccessService {
  private readonly _store = inject(Store);
  private readonly _capabilities = inject(ApplicationCapabilitiesService);
  private readonly _authenticated = this._store.selectSignal(
    selectIsFullyAuthenticated
  );

  constructor() {
    effect(() => {
      const authenticated = this._authenticated();
      untracked(() => {
        if (authenticated) {
          void this._capabilities.refresh();
        } else {
          this._capabilities.reset();
        }
      });
    });
    effect(() => {
      const capabilities = presentedCapabilities(this._capabilities.state());
      untracked(() =>
        this._store.dispatch(
          adminActions.applicationCapabilitiesPresented({ capabilities })
        )
      );
    });
  }
}

/** What an admin route allows where PM authority does not decide (the pre-M2a guard). */
export type LegacyRouteAccess = 'claim-administrator' | 'unguarded';

/**
 * Direct-navigation guard for an application capability (M2a). On every entry it re-reads the
 * capabilities (never a remembered answer) and then: where PM authority decides, allows only
 * what the API allows; where it does not (off/shadow 404, or the read failed), applies exactly
 * the guard the route had before M2a. Refusals go to /not-found, as before.
 */
export function requireApplicationCapability(
  capability: ApplicationCapabilityName,
  legacy: LegacyRouteAccess
): CanActivateFn {
  return async () => {
    const store = inject(Store);
    const router = inject(Router);
    const capabilities = inject(ApplicationCapabilitiesService);
    // Keeps the store's presentation in step with the answer this guard is about to read.
    inject(ApplicationAccessService);

    await firstValueFrom(store.select(selectAuthIsIdle).pipe(filter(Boolean)));
    const authenticated = await firstValueFrom(
      store.select(selectIsFullyAuthenticated).pipe(take(1))
    );
    const presented = authenticated
      ? presentedCapabilities(await capabilities.refresh())
      : null;

    let allowed: boolean;
    if (presented !== null) {
      allowed = presented[capability] === true;
    } else if (legacy === 'unguarded') {
      allowed = true;
    } else {
      allowed = await firstValueFrom(
        store.select(selectIsSyrfAdminUserAuthenticated).pipe(take(1))
      );
    }
    return allowed || router.createUrlTree(['/not-found']);
  };
}
